import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import {
    ArrowForwardOutlined,
    LockOutlined,
    MailOutlineOutlined,
    ShieldOutlined,
    VisibilityOffOutlined,
    VisibilityOutlined
} from "@mui/icons-material";
import {setCredentials} from "./authSlice";
import {useAskCodeMutation, useLoginMutation} from "./authApiSlice";
import "./login.css";

const Login = () => {
    const emailRef = useRef();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailCode, setEmailCode] = useState("");
    const [errMsg, setErrMsg] = useState("");
    const [showCode, setShowCode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [login, {isLoading: isLoginLoading}] = useLoginMutation();
    const [askCode, {isLoading: isCodeLoading}] = useAskCodeMutation();
    const dispatch = useDispatch();

    const isLoading = isLoginLoading || isCodeLoading;
    const from = location.state?.from?.pathname || "/panel";

    useEffect(() => {
        emailRef.current?.focus();
    }, []);

    useEffect(() => {
        setErrMsg("");
    }, [email, password, emailCode]);

    const describeError = (err) => {
        const status = err?.originalStatus || err?.status;
        if (!status) {
            return "Сервер не отвечает. Проверьте соединение и попробуйте ещё раз.";
        }
        if (status === 400) {
            return "Введите email и пароль.";
        }
        if (status === 401) {
            return showCode
                ? "Неверный или истёкший код. Запросите новый код."
                : "Неверный email или пароль.";
        }
        if (status === 412) {
            return "Код уже отправлен. Проверьте почту — он действует 10 минут.";
        }
        return "Не удалось войти. Попробуйте ещё раз.";
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isLoading || !email.trim() || !password) {
            return;
        }

        try {
            if (!showCode) {
                await askCode({email, password}).unwrap();
                setShowCode(true);
            } else {
                const userData = await login({email, password, emailCode}).unwrap();
                dispatch(setCredentials({...userData, email}));
                setEmail("");
                setPassword("");
                setEmailCode("");
                navigate(from, {replace: true});
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            const status = err?.originalStatus || err?.status;
            if (!showCode && status === 412) {
                // Код уже был отправлен ранее и ещё действует — переходим к вводу.
                setShowCode(true);
            }
            setErrMsg(describeError(err));
        }
    };

    return (
        <div className="login">
            <aside className="login__brand">
                <div className="login__brand-inner">
                    <div className="login__logo">
                        <span className="login__logo-mark">i</span>
                        <span className="login__logo-text">
                            <span><span className="login__logo-accent">i</span>Study</span>
                            <small>Admin Console</small>
                        </span>
                    </div>

                    <h1 className="login__headline">
                        Управляйте сайтом, тестами и заявками в одном месте
                    </h1>
                    <p className="login__lead">
                        Единая панель для контента, учебных заведений, клиентских заявок,
                        тестирования и рассылок.
                    </p>

                    <ul className="login__features">
                        <li><ShieldOutlined/> Двухшаговый вход с кодом на email</li>
                        <li><LockOutlined/> Защищённые сессии и роли</li>
                        <li><ArrowForwardOutlined/> Быстрый переход к любому разделу</li>
                    </ul>

                    <div className="login__brand-footer">
                        © {new Date().getFullYear()} iStudy · Внутренняя система
                    </div>
                </div>
            </aside>

            <main className="login__main">
                <div className="login__card">
                    <div className="login__card-header">
                        <h2>Вход в панель</h2>
                        <p>
                            {showCode
                                ? "Введите код из письма — он действует 10 минут."
                                : "Введите рабочий email и пароль. Мы отправим код подтверждения."}
                        </p>
                    </div>

                    <form className="login__form" onSubmit={handleSubmit} noValidate>
                        <label className="login__field">
                            <span className="login__label">Email</span>
                            <span className="login__control">
                                <MailOutlineOutlined fontSize="small"/>
                                <input
                                    ref={emailRef}
                                    name="email"
                                    type="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    placeholder="name@istudy.com.ru"
                                    autoComplete="username"
                                    disabled={showCode || isLoading}
                                    required
                                />
                            </span>
                        </label>


                        <label className="login__field">
                            <span className="login__label">Пароль</span>
                            <span className="login__control">
                                <LockOutlined fontSize="small"/>
                                <input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    disabled={showCode || isLoading}
                                    required
                                />
                                <button
                                    type="button"
                                    className="login__reveal"
                                    onClick={() => setShowPassword((value) => !value)}
                                    aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                                    tabIndex={showCode ? -1 : 0}
                                >
                                    {showPassword
                                        ? <VisibilityOffOutlined fontSize="small"/>
                                        : <VisibilityOutlined fontSize="small"/>}
                                </button>
                            </span>
                        </label>

                        {showCode && (
                            <label className="login__field">
                                <span className="login__label">Код из письма</span>
                                <span className="login__control">
                                    <ShieldOutlined fontSize="small"/>
                                    <input
                                        name="emailCode"
                                        type="text"
                                        value={emailCode}
                                        onChange={(event) => setEmailCode(event.target.value)}
                                        placeholder="Например, aB3dE9xQ2z"
                                        autoComplete="one-time-code"
                                        autoFocus
                                        required
                                    />
                                </span>
                            </label>
                        )}

                        {errMsg && (
                            <div className="login__error" role="alert">{errMsg}</div>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary login__submit"
                            disabled={isLoading || !email.trim() || !password || (showCode && !emailCode.trim())}
                        >
                            {isLoading
                                ? (showCode ? "Проверяем код…" : "Отправляем код…")
                                : (showCode ? "Войти" : "Получить код")}
                        </button>

                        {showCode && (
                            <button
                                type="button"
                                className="login__secondary"
                                onClick={() => {
                                    setShowCode(false);
                                    setEmailCode("");
                                    setErrMsg("");
                                }}
                                disabled={isLoading}
                            >
                                Изменить email или пароль
                            </button>
                        )}
                    </form>

                    <div className="login__hint">
                        Нет доступа? Напишите администратору — он выдаст роль в разделе
                        «Пользователи».
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Login;
